import { useState, useEffect } from 'react';
import { useFirebase } from '../lib/firebase/FirebaseProvider';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase/config';
import { Report } from '../hooks/useReports';
import TreasureCard from '../components/TreasureCard';
import { GamificationGate } from '../components/GamificationGate';
import ShareModal from '../components/ShareModal';

interface ProfileEditData {
  displayName: string;
}

const ProfilePage = () => {
  const { user, userProfile, loading: authLoading } = useFirebase();
  const [userReports, setUserReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState<ProfileEditData>({
    displayName: ''
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareReport, setShareReport] = useState<Report | null>(null);

  // Initialize edit data when user profile loads
  useEffect(() => {
    if (userProfile) {
      setEditData({
        displayName: userProfile.displayName || ''
      });
    }
  }, [userProfile]);

  // Fetch user's reports
  useEffect(() => {
    const fetchUserReports = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const getUserReports = httpsCallable(functions, 'getUserReports');
        const result = await getUserReports({
          userId: user.uid,
          limit: 50
        });

        const data = result.data as { reports: Report[] };
        setUserReports(data.reports || []);
      } catch (err: any) {
        console.error('Error fetching user reports:', err);
        setError('Failed to load your reports');
      } finally {
        setLoading(false);
      }
    };

    fetchUserReports();
  }, [user]);

  const handleEditProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setEditLoading(true);
      setEditError(null);

      const updateUserProfile = httpsCallable(functions, 'updateUserProfile');
      await updateUserProfile({
        updates: editData
      });

      setShowEditModal(false);
      // The user profile will be updated automatically through the auth hook
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setEditError(err.message || 'Failed to update profile');
    } finally {
      setEditLoading(false);
    }
  };

  const handleShare = (reportId: string) => {
    const report = userReports.find(r => r.id === reportId);
    if (report) {
      setShareReport(report);
      setShareModalOpen(true);
    }
  };

  const handleCloseShareModal = () => {
    setShareModalOpen(false);
    setShareReport(null);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-sky flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-cardboard rounded-full animate-pulse mx-auto mb-4"></div>
          <p className="text-urban">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-sky flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-gradient-to-br from-cardboard to-cardboard-600 rounded-full flex items-center justify-center text-white text-2xl mx-auto mb-6">
            👤
          </div>
          <h1 className="text-2xl font-bold text-urban mb-4">Sign In Required</h1>
          <p className="text-urban/70 mb-6">
            You need to sign in to view your profile and activity.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Profile Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-urban/10 p-8 mb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Avatar */}
            <div className="w-24 h-24 bg-gradient-to-br from-cardboard to-cardboard-600 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-lg">
              {(userProfile?.displayName || user.displayName || user.email || '?').charAt(0).toUpperCase()}
            </div>

            {/* User Info */}
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-urban mb-2">
                    {userProfile?.displayName || user.displayName || 'Treasure Hunter'}
                  </h1>
                  <p className="text-urban/70 mb-4">{user.email}</p>
                  
                  {/* Stats */}
                  {userProfile && (
                    <div className="flex flex-wrap items-center gap-6">
                      <GamificationGate feature="coins">
                        <div className="flex items-center gap-2">
                          <span className="text-treasure text-xl">🪙</span>
                          <span className="font-semibold text-urban">{userProfile.goldCoins}</span>
                          <span className="text-urban/70 text-sm">Gold Coins</span>
                        </div>
                      </GamificationGate>
                      <GamificationGate feature="coins">
                        <div className="flex items-center gap-2">
                          <span className="text-cardboard text-xl">🎫</span>
                          <span className="font-semibold text-urban">{userProfile.farAwayTokens}</span>
                          <span className="text-urban/70 text-sm">Far-Away Tokens</span>
                        </div>
                      </GamificationGate>
                      <div className="flex items-center gap-2">
                        <span className="text-verge text-xl">📊</span>
                        <span className="font-semibold text-urban">{userReports.length}</span>
                        <span className="text-urban/70 text-sm">Reports Submitted</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Edit Button */}
                <button
                  onClick={() => setShowEditModal(true)}
                  className="bg-cardboard hover:bg-cardboard-600 text-white font-semibold px-6 py-3 rounded-full transition-all duration-200 hover:shadow-md active:scale-95 flex items-center gap-2"
                >
                  <span>✏️</span>
                  Edit Profile
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Activity Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-urban/10 p-8">
          <h2 className="text-2xl font-bold text-urban mb-6">Your Treasure Reports</h2>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-teddy rounded-2xl h-80 animate-pulse"></div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-rust/20 rounded-full flex items-center justify-center text-rust text-2xl mx-auto mb-4">
                ⚠️
              </div>
              <h3 className="text-xl font-semibold text-urban mb-2">Error Loading Reports</h3>
              <p className="text-urban/70 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="bg-cardboard hover:bg-cardboard-600 text-white font-semibold px-6 py-3 rounded-full transition-all duration-200"
              >
                Try Again
              </button>
            </div>
          ) : userReports.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-cardboard/20 rounded-full flex items-center justify-center text-cardboard text-2xl mx-auto mb-4">
                📦
              </div>
              <h3 className="text-xl font-semibold text-urban mb-2">No Reports Yet</h3>
              <p className="text-urban/70 mb-6">
                You haven't submitted any treasure reports yet. Start exploring and sharing your finds!
              </p>
              <a
                href="/submit"
                className="bg-cardboard hover:bg-cardboard-600 text-white font-semibold px-6 py-3 rounded-full transition-all duration-200 hover:shadow-md active:scale-95 inline-flex items-center gap-2"
              >
                <span>📤</span>
                Submit Your First Find
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {userReports.map((report) => (
                <TreasureCard 
                  key={report.id} 
                  report={report} 
                  onShare={handleShare}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-urban">Edit Profile</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 text-urban/70 hover:text-urban hover:bg-teddy rounded-full transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleEditProfile} className="space-y-6">
                {editError && (
                  <div className="bg-rust/10 border border-rust/20 rounded-lg p-4">
                    <p className="text-rust text-sm">{editError}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="displayName" className="block text-sm font-semibold text-urban mb-2">
                    Display Name
                  </label>
                  <input
                    type="text"
                    id="displayName"
                    value={editData.displayName}
                    onChange={(e) => setEditData({ ...editData, displayName: e.target.value })}
                    className="w-full px-4 py-3 border border-urban/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-cardboard focus:border-transparent transition-all duration-200"
                    placeholder="Enter your display name"
                    required
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 px-6 py-3 border border-urban/20 text-urban font-semibold rounded-lg hover:bg-teddy transition-all duration-200"
                    disabled={editLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editLoading}
                    className="flex-1 bg-cardboard hover:bg-cardboard-600 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 hover:shadow-md active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {shareReport && (
        <ShareModal
          isOpen={shareModalOpen}
          onClose={handleCloseShareModal}
          reportId={shareReport.id}
          reportTitle={shareReport.tags?.join(', ') || 'Curbside Treasure'}
          reportImage={shareReport.images?.[0]?.url}
          reportLocation={`${shareReport.location.suburb}, ${shareReport.location.postcode}`}
        />
      )}
    </div>
  );
};

export default ProfilePage;